import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, Palette, RotateCcw, Image as ImageIcon } from 'lucide-react';

type InvertMode = 'full' | 'selective' | 'hue';
type OutputFormat = 'png' | 'jpg';

const MODES: { value: InvertMode; label: string; description: string }[] = [
  { value: 'full', label: 'Full Invert', description: 'Invert all color channels' },
  { value: 'selective', label: 'Selective Channel', description: 'Choose which channels to invert' },
  { value: 'hue', label: 'Hue Invert', description: 'Shift hue by 180 degrees' },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1/3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1/3) * 255),
  ];
}

const InvertColors: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [mode, setMode] = useState<InvertMode>('full');
  const [intensity, setIntensity] = useState(100);
  const [invertR, setInvertR] = useState(true);
  const [invertG, setInvertG] = useState(true);
  const [invertB, setInvertB] = useState(true);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [jpgQuality, setJpgQuality] = useState(92);
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ w: number; h: number } | null>(null);
  const [showBeforeAfter, setShowBeforeAfter] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    setFile(f);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setOriginalPreview(url);
      const img = new window.Image();
      img.onload = () => {
        setImgElement(img);
        setImageDimensions({ w: img.naturalWidth, h: img.naturalHeight });
      };
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const applyInvert = useCallback((canvas: HTMLCanvasElement, img: HTMLImageElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = img.naturalWidth;
    const h = img.naturalHeight;
    canvas.width = w;
    canvas.height = h;

    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, w, h);
    const data = imageData.data;
    const blend = intensity / 100;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i], g = data[i + 1], b = data[i + 2];
      let nr = r, ng = g, nb = b;

      if (mode === 'full') {
        nr = 255 - r;
        ng = 255 - g;
        nb = 255 - b;
      } else if (mode === 'selective') {
        nr = invertR ? 255 - r : r;
        ng = invertG ? 255 - g : g;
        nb = invertB ? 255 - b : b;
      } else if (mode === 'hue') {
        const [h, s, l] = rgbToHsl(r, g, b);
        const newH = (h + 0.5) % 1;
        [nr, ng, nb] = hslToRgb(newH, s, l);
      }

      // Blend with original based on intensity
      data[i] = Math.round(r + (nr - r) * blend);
      data[i + 1] = Math.round(g + (ng - g) * blend);
      data[i + 2] = Math.round(b + (nb - b) * blend);
    }

    ctx.putImageData(imageData, 0, 0);
  }, [mode, intensity, invertR, invertG, invertB]);

  useEffect(() => {
    if (!imgElement || !previewCanvasRef.current) return;
    applyInvert(previewCanvasRef.current, imgElement);
  }, [imgElement, applyInvert]);

  const handleDownload = useCallback(() => {
    if (!imgElement) return;
    const canvas = document.createElement('canvas');
    applyInvert(canvas, imgElement);
    const mimeType = outputFormat === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = outputFormat === 'jpg' ? jpgQuality / 100 : undefined;
    const dataUrl = canvas.toDataURL(mimeType, quality);
    const link = document.createElement('a');
    const baseName = file?.name?.replace(/\.[^.]+$/, '') || 'inverted';
    link.download = `${baseName}-inverted.${outputFormat === 'jpg' ? 'jpg' : 'png'}`;
    link.href = dataUrl;
    link.click();
  }, [imgElement, applyInvert, outputFormat, jpgQuality, file]);

  const handleReset = useCallback(() => {
    setFile(null);
    setOriginalPreview(null);
    setImgElement(null);
    setImageDimensions(null);
    setIntensity(100);
    setMode('full');
    setInvertR(true);
    setInvertG(true);
    setInvertB(true);
    setError(null);
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Palette className="w-6 h-6 text-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-900">Invert Colors</h1>
        </div>
        <p className="text-gray-600">Invert image colors with full, selective channel, or hue inversion modes.</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {!file ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors"
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-1">Drop an image here or click to upload</p>
          <p className="text-sm text-gray-500">Supports PNG, JPG, WebP, GIF</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Settings</h2>
              <button onClick={handleReset} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                <RotateCcw className="w-4 h-4" /> Reset
              </button>
            </div>

            {/* Mode selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Invert Mode</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {MODES.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => setMode(m.value)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      mode === m.value
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <div className="font-medium text-sm">{m.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{m.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Selective channel toggles */}
            {mode === 'selective' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Channels to Invert</label>
                <div className="flex gap-3">
                  {[
                    { label: 'Red', checked: invertR, onChange: setInvertR, color: 'text-red-600' },
                    { label: 'Green', checked: invertG, onChange: setInvertG, color: 'text-green-600' },
                    { label: 'Blue', checked: invertB, onChange: setInvertB, color: 'text-blue-600' },
                  ].map((ch) => (
                    <label
                      key={ch.label}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                        ch.checked ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={ch.checked}
                        onChange={(e) => ch.onChange(e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className={`text-sm font-medium ${ch.color}`}>{ch.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Intensity slider */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Intensity (blend)</label>
                <span className="text-sm font-mono text-indigo-600">{intensity}%</span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                value={intensity}
                onChange={(e) => setIntensity(Number(e.target.value))}
                className="w-full accent-indigo-600"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Original</span>
                <span>Fully Inverted</span>
              </div>
            </div>

            {/* Output format */}
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Output Format</label>
                <select
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value as OutputFormat)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="png">PNG</option>
                  <option value="jpg">JPG</option>
                </select>
              </div>
              {outputFormat === 'jpg' && (
                <div className="flex-1 min-w-[150px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quality: {jpgQuality}%</label>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    value={jpgQuality}
                    onChange={(e) => setJpgQuality(Number(e.target.value))}
                    className="w-full accent-indigo-600"
                  />
                </div>
              )}
            </div>

            {/* Before/After toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="beforeAfterInvert"
                checked={showBeforeAfter}
                onChange={(e) => setShowBeforeAfter(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="beforeAfterInvert" className="text-sm text-gray-700">Show before/after side-by-side</label>
            </div>
          </div>

          {/* Image info */}
          {imageDimensions && file && (
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1"><ImageIcon className="w-4 h-4" /> {imageDimensions.w} x {imageDimensions.h}px</span>
              <span>{formatFileSize(file.size)}</span>
              <span>{file.name}</span>
            </div>
          )}

          {/* Preview */}
          <div className={`grid gap-4 ${showBeforeAfter ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
            {showBeforeAfter && originalPreview && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Original</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                  <img src={originalPreview} alt="Original" className="w-full h-auto" />
                </div>
              </div>
            )}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Inverted</h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                <canvas ref={previewCanvasRef} className="w-full h-auto" />
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              <Download className="w-5 h-5" /> Download Inverted Image
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvertColors;
